import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, FileText, Loader2, Sparkles, UploadCloud, XCircle } from "lucide-react";

const ResultRow = ({ r, i }) => (
  <li className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`grades-ai-result-${i}`}>
    <div className="flex items-start gap-2">
      {r.status === "OK" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-800">{r.class_name ? `Turma ${r.class_name}` : r.file}{r.subject ? ` · ${r.subject}` : ""}{r.term_order ? ` · ${r.term_order}º tri` : ""}</div>
        {r.status === "OK" ? (
          <div className="text-xs text-slate-600"><b className="text-green-700">{r.updated} aluno(s) atualizado(s)</b> de {r.total_pdf} no PDF · {r.faltas} com faltas{r.replaced ? ` · ${r.replaced} nota(s) anteriores substituídas` : ""}</div>
        ) : <div className="text-xs text-red-600">{r.error}</div>}
        {r.not_found?.length > 0 && <div className="mt-1 text-xs text-amber-700">Não encontrados na turma: {r.not_found.join(", ")}</div>}
        <div className="text-[11px] text-slate-400">{r.file}</div>
      </div>
    </div>
  </li>
);

export const GradesAiImport = ({ classId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const run = async () => {
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/classes/${classId}/grades/ai-import`, fd, { timeout: 300000 });
      let j = { status: "PROCESSANDO", total: data.total, done: 0, results: [] };
      setJob(j);
      while (j.status !== "CONCLUIDO") {
        await new Promise((r) => setTimeout(r, 3000));
        j = (await api.get(`/grades/ai-import/${data.id}`)).data;
        setJob(j);
      }
      toast.success("Importação de notas concluída"); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setJob(null); } finally { setBusy(false); }
  };
  const done = job?.status === "CONCLUIDO";
  const total = done ? job.results.reduce((s, r) => s + (r.updated || 0), 0) : 0;
  return (
    <>
      <Button variant="outline" onClick={() => { setFiles([]); setJob(null); setOpen(true); }} className="border-blue-600 text-blue-700" data-testid="grades-ai-import-btn">
        <Sparkles className="mr-2 h-4 w-4" />Importar Notas com IA
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-xl" data-testid="grades-ai-import-dialog">
          <DialogHeader><DialogTitle className="pr-6">Importar Notas com IA</DialogTitle></DialogHeader>
          {done ? (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto" data-testid="grades-ai-summary">
              <div className="rounded-lg bg-green-50 p-3 text-center"><div className="text-2xl font-bold text-green-700" data-testid="grades-ai-total">{total}</div><div className="text-xs text-green-700">lançamento(s) de aluno atualizados</div></div>
              <ul className="space-y-2">{job.results.map((r, i) => <ResultRow key={i} r={r} i={i} />)}</ul>
            </div>
          ) : job ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="grades-ai-progress">
              <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
              <p className="text-sm text-slate-600">A IA está lendo as notas... {job.done || 0} de {job.total} arquivo(s)</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Envie os PDFs de notas do Conexão Educação. A IA identifica turma, disciplina, trimestre, nota final e faltas. A nota é dividida em Trabalho (20%), Teste (30%) e Prova (50%); as faltas entram na frequência do diário. Notas já lançadas são substituídas.</p>
              <input ref={input} type="file" multiple hidden accept=".pdf,application/pdf" onChange={(e) => { setFiles([...e.target.files]); e.target.value = ""; }} data-testid="grades-ai-file-input" />
              <button type="button" onClick={() => input.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-sm text-slate-500 hover:border-blue-400 hover:bg-blue-50/40" data-testid="grades-ai-pick-btn">
                <UploadCloud className="h-7 w-7 text-blue-700" />Selecionar PDFs (um ou vários)
              </button>
              {files.length > 0 && <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">{files.map((f) => <li key={f.name} className="flex items-center gap-1.5 text-slate-700"><FileText className="h-4 w-4 text-slate-400" />{f.name}</li>)}</ul>}
            </div>
          )}
          <DialogFooter>
            {done ? <Button onClick={() => setOpen(false)} data-testid="grades-ai-close">Fechar</Button> : (
              <Button onClick={run} disabled={busy || !files.length} className="bg-blue-700 hover:bg-blue-800" data-testid="grades-ai-run-btn">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Importar {files.length || ""} arquivo(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
